import { BuildOutlined } from '@ant-design/icons';
import { Badge, Tabs, theme, Typography } from 'antd';
import { errorColor, primaryColor, successColor, warningColor } from 'assets/theme/colors';
import { useAscensorEstados } from 'providers/AscensorEstadosProvider';
import { useMaintanceToDo } from 'providers/MaintanceToDoProvider';
import React, { useEffect, useMemo, useState } from 'react';

// Heurística nombre→semáforo para el estado del ascensor.
function estadoColor(nombre = '') {
  const n = nombre.toLowerCase();
  if (n.includes('operativo')) return successColor;
  if (n.includes('observ') || n.includes('mantenim')) return warningColor;
  if (n.includes('fuera') || n.includes('detenid')) return errorColor;
  return primaryColor;
}

const AscensoresMaintanceToDoForm = ({ ascensores }) => {
  const { call, hasCalled, isLoading, data } = useAscensorEstados();
  useEffect(() => {
    if (!hasCalled) {
      call();
    }
  }, [call, hasCalled]);
  const { handleAscensoresEstados } = useMaintanceToDo();
  const { token } = theme.useToken();

  // Seed local selection from prop; coerce estado to Number (may arrive as string/null)
  const [sel, setSel] = useState(() => {
    const m = {};
    (ascensores || []).forEach((a) => {
      m[a.id] = Number(a.estado) || null;
    });
    return m;
  });

  const handlePick = (ascId, estadoId) => {
    setSel((prev) => {
      const next = { ...prev, [ascId]: estadoId };
      handleAscensoresEstados(
        (ascensores || []).map((a) => ({ id: a.id, estado: next[a.id] ?? null }))
      );
      return next;
    });
  };

  const [activeTab, setActiveTab] = useState('pendientes');

  const { total, configurados, pendientes, listaConfigurados, listaPendientes } = useMemo(() => {
    const all = ascensores || [];
    const conf = all.filter((a) => sel[a.id] != null);
    const pend = all.filter((a) => sel[a.id] == null);
    return {
      total: all.length,
      configurados: conf.length,
      pendientes: pend.length,
      listaConfigurados: conf,
      listaPendientes: pend,
    };
  }, [ascensores, sel]);

  const visibleList = activeTab === 'configurados' ? listaConfigurados : listaPendientes;

  const tabItems = [
    {
      key: 'pendientes',
      label: (
        <span>
          Pendientes{' '}
          <Badge count={pendientes} style={{ backgroundColor: token.colorWarning }} size="small" />
        </span>
      ),
    },
    {
      key: 'configurados',
      label: (
        <span>
          Configurados{' '}
          <Badge
            count={configurados}
            style={{ backgroundColor: token.colorSuccess }}
            size="small"
          />
        </span>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: token.marginMD }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: token.marginSM,
          paddingBottom: token.paddingSM,
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
        }}
      >
        <div
          style={{
            background: token.colorPrimaryBg,
            border: `1px solid ${token.colorPrimaryBorder}`,
            borderRadius: token.borderRadiusLG,
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <BuildOutlined style={{ color: token.colorPrimary, fontSize: 18 }} />
        </div>
        <div>
          <Typography.Title level={5} style={{ margin: 0, lineHeight: 1.3 }}>
            Estados de Ascensores
          </Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
            {total} ascensor{total !== 1 ? 'es' : ''} • {configurados} configurado
            {configurados !== 1 ? 's' : ''}
          </Typography.Text>
        </div>
      </div>

      {/* Tabs */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        size="small"
        style={{ marginBottom: 0 }}
      />

      {/* Ascensor cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: token.marginSM }}>
        {visibleList.length === 0 && (
          <Typography.Text
            type="secondary"
            style={{ textAlign: 'center', padding: `${token.paddingLG}px 0` }}
          >
            {activeTab === 'configurados'
              ? 'Ningún ascensor configurado todavía.'
              : 'Todos los ascensores están configurados.'}
          </Typography.Text>
        )}

        {visibleList.map((asc) => {
          const selectedId = sel[asc.id];
          const isConfigured = selectedId != null;

          return (
            <div
              key={asc.id}
              style={{
                border: `1px solid ${token.colorBorderSecondary}`,
                borderRadius: token.borderRadiusLG,
                padding: token.paddingMD,
                background: token.colorBgContainer,
                display: 'flex',
                flexDirection: 'column',
                gap: token.marginSM,
              }}
            >
              {/* Card header */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: token.marginSM,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: token.marginSM }}>
                  <div
                    style={{
                      background: token.colorPrimaryBg,
                      border: `1px solid ${token.colorPrimaryBorder}`,
                      borderRadius: token.borderRadius,
                      width: 36,
                      height: 36,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <BuildOutlined style={{ color: token.colorPrimary, fontSize: 16 }} />
                  </div>
                  <div>
                    <Typography.Text strong style={{ display: 'block', lineHeight: 1.3 }}>
                      {asc.nombre}
                    </Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                      Ascensor #{asc.id}
                    </Typography.Text>
                  </div>
                </div>

                {/* Pill */}
                <span
                  style={{
                    fontSize: token.fontSizeSM,
                    fontWeight: 500,
                    padding: '2px 10px',
                    borderRadius: token.borderRadiusFull ?? 999,
                    background: isConfigured ? token.colorSuccessBg : token.colorWarningBg,
                    color: isConfigured ? token.colorSuccessText : token.colorWarningText,
                    border: `1px solid ${
                      isConfigured ? token.colorSuccessBorder : token.colorWarningBorder
                    }`,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {isConfigured ? 'Configurado' : 'Pendiente'}
                </span>
              </div>

              {/* Estado buttons */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns:
                    (data?.length ?? 0) > 6 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                  gap: token.marginXS,
                }}
              >
                {isLoading &&
                  [1, 2, 3].map((i) => (
                    <div
                      key={i}
                      style={{
                        height: 34,
                        borderRadius: token.borderRadius,
                        background: token.colorFillTertiary,
                      }}
                    />
                  ))}

                {!isLoading &&
                  (data || []).map((opt) => {
                    const active = selectedId === opt.id;
                    const color = estadoColor(opt.nombre);
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => handlePick(asc.id, opt.id)}
                        style={{
                          cursor: 'pointer',
                          padding: `${token.paddingXS}px ${token.paddingSM}px`,
                          borderRadius: token.borderRadius,
                          fontSize: token.fontSizeSM,
                          fontWeight: active ? 600 : 400,
                          lineHeight: 1.4,
                          textAlign: 'center',
                          transition: 'all 0.15s',
                          border: active
                            ? `1.5px solid ${color}`
                            : `1px solid ${token.colorBorder}`,
                          background: active ? color : token.colorBgContainer,
                          color: active ? '#fff' : token.colorText,
                        }}
                      >
                        {opt.nombre}
                      </button>
                    );
                  })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AscensoresMaintanceToDoForm;
